import { updateFile, changeActiveFileMetaInfo } from '../../features/organization/organizationSlice';
import SpaceEditorSkeleton from '../UserSpace/components/SpaceEditorSkeleton/SpaceEditorSkeleton';
import { CmsEditor } from '../../../../editor/src/components/Editor/CmsEditor';
import { EditorHeader } from '../../../../editor/src/components/EditorHeader';
import DocumentComments from './ExtraFeature/DocumentComments';
import { calculateReadTime } from '../../utils/common';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import React, { useCallback, useMemo } from 'react';
import ChatbotUI from './../AIChatBot/ChatbotUi';
import SwaggerView from '../SwaggerView';
import { Divider, Flex } from 'antd';
import { theme } from 'antd';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const { useToken } = theme;

const EditorSpace = () => {
  const dispatch = useDispatch();
  const [edit, editorRef, setOpenApiFile, onTransactionChange, lastSavedContentRef, mainTheme, spaceEditorRef] =
    useOutletContext();
  const { loading, activeWorkspace, activeFile } = useSelector((state) => state.organization);
  const { user, userOrganizationId } = useSelector((state) => state.user);
  const { comments } = useSelector((state) => state.editor);
  const file_id = activeFile?.id;
  const isApiFile = activeFile && Object.keys(activeFile).includes('openapi');
  const workspace_id = activeFile?.workspace_id;
  const spaceLoading = loading?.fetchFileById;
  const isGitBasedWorkspace = activeWorkspace?.is_git_based;
  const { token } = useToken();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const activeContent = useMemo(() => {
    if (isGitBasedWorkspace) return activeFile?.data;
    if (edit) return activeFile?.draft;
    return activeFile?.data;
  }, [isGitBasedWorkspace, edit, activeFile]);

  const updateFilename = useCallback(
    (fileName) => {
      if (fileName && fileName !== activeFile?.title) {
        const data = {
          title: fileName,
        };
        dispatch(updateFile({ data, workspace_id, file_id }));
      }
    },
    [activeFile?.title]
  );

  const metaData = {
    author: activeFile?.file_owner?.name || activeFile?.created_by,
    createdAt: new Date(activeFile?.created_at),
    lastUpdate: new Date(activeFile?.updated_at),
    lastUpdatedBy: activeFile?.updated_by,
    totalComments: comments?.length || 0,
    fileName: activeFile?.title,
    readTime: isGitBasedWorkspace ? 3 : calculateReadTime(activeFile?.data),
    workspaceTitle: activeWorkspace?.title,
  };

  const onFileMetaChange = useCallback(
    async ({ type, value }, onError) => {
      const metaInfo = { [type]: value };
      dispatch(changeActiveFileMetaInfo({ id: file_id, metaInfo })).then(({ payload }) => {
        if (!payload || payload?.status_code !== 200) {
          onError?.();
        }
      });
    },
    [file_id, workspace_id]
  );

  if (spaceLoading) {
    return <SpaceEditorSkeleton />;
  }

  return isApiFile ? (
    <>
      <SwaggerView swaggerData={activeFile} isEditable={edit} setOpenApiFile={setOpenApiFile} />
      <ChatbotUI />
    </>
  ) : (
    <>
      <div className="editor__container">
        {!isGitBasedWorkspace && (
          <EditorHeader user={user} isEditable={edit} selectedFile={activeFile} onChange={onFileMetaChange} />
        )}
        <Flex
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          <CmsEditor
            file_id={file_id}
            workspace_id={workspace_id}
            organization_id={userOrganizationId}
            isEditable={edit}
            // userDetail={userDetail}
            editorRef={editorRef}
            editorContent={activeContent}
            isGitBasedWorkspace={isGitBasedWorkspace}
            lastSavedContentRef={lastSavedContentRef}
            metaData={metaData}
            updateFilename={updateFilename}
            theme={mainTheme}
            user={user}
            autoSave={true}
            container={spaceEditorRef.current}
            className="document-editor"
            onTransactionChange={onTransactionChange}
          />
        </Flex>
        {!edit && (
          <Flex align="flex-start" justify="center" style={{ width: '100%' }}>
            <Flex
              vertical
              style={{
                maxWidth: isMobile ? '100%' : 'var(--remirror-editor-width)',
                width: '100%',
              }}
            >
              <Divider
                style={{
                  borderBlockWidth: '1px',
                  borderBlockColor: token.colorBorder,
                }}
              />
              <DocumentComments />
            </Flex>
            {!isMobile && <div style={{ minWidth: '180px', maxWidth: '180px' }}></div>}
          </Flex>
        )}
      </div>
      {!isMobile && <ChatbotUI />}
    </>
  );
};

export default EditorSpace;
